import { faGalaxy } from '@fortawesome/pro-solid-svg-icons/faGalaxy';
import {
	archiveThreads,
	deleteThreads,
	GetItemCounts,
	InfiniteData,
	MessageItemUpdateType,
	PaginatedResponseType,
	useInfiniteMessageCentre,
	useInfiniteMessageCentreSearchResults,
	useUpdateMessageCentre,
} from '@ukpostbox/client-data';
import { Colour, useComboBoxSimple, useLayout, useNotifications } from '@ukpostbox/frontend-framework';
import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { SearchMessageCentreModal } from '../Components/Search/Modals/SearchMessageCentreModal';

import useThreadActions from '../Hooks/useThreadActions';
import { MessageCentreContextType, MessageCentreProviderType } from '../Types/MessageCentreProviderTypes';
import { useMessageCentreStore } from './Stores/MessageCentreStore';

const MessageCentreContext = React.createContext<MessageCentreContextType>({} as MessageCentreContextType);

function MessageCentreProvider(props: PropsWithChildren<MessageCentreProviderType>) {
	const params = useParams();
	const navigate = useNavigate();

	const { AddNotification } = useNotifications();
	const { addGlobalDocumentValue } = useLayout();

	const {
		allSelected,
		visibleSelected,
		visibleIndeterminate,
		selectedItems,
		selectedItemsRef,
		ToggleAll,
		ToggleVisible,
		ToggleSelected,
		ResetSelection,
	} = useComboBoxSimple();

	const dispatchMessageCentreStoreUpdate = useMessageCentreStore((state) => state.dispatchMessageCentreStoreUpdate);

	const folderMappedId = useMessageCentreStore((state) => state.folderMappedId);
	const showFilterModal = useMessageCentreStore((state) => state.showFilterModal);

	const [urlSearchParams, setUrlSearchParams] = useSearchParams();

	const {
		data: messageCentreData,
		error: messageCentreError,
		isError: messageCentreIsError,
		isLoading: messageCentreIsLoading,
		isSuccess: messageCentreIsSuccess,
		isFetchingNextPage: messageCentreIsFetchingNextPage,
		fetchNextPage: messageCentreFetchNextPage,
		hasNextPage: messageCentreHasNextPage,
		dataUpdatedAt: messageCentreDataUpdatedAt,
		isRefetching: messageCentreIsRefetching,
		isStale: messageCentreIsStale,
	} = useInfiniteMessageCentre(folderMappedId);

	const {
		data: messageResultsData,
		error: messageResultsError,
		isError: messageResultsIsError,
		isLoading: messageResultsIsLoading,
		isSuccess: messageResultsIsSuccess,
		isFetchingNextPage: messageResultsIsFetchingNextPage,
		fetchNextPage: messageResultsFetchNextPage,
		hasNextPage: messageResultsHasNextPage,
		dataUpdatedAt: messageResultsDataUpdatedAt,
		isRefetching: messageResultsIsRefetching,
		isStale: messageResultsIsStale,
	} = useInfiniteMessageCentreSearchResults(urlSearchParams);

	const messageCentreItemCounts = GetItemCounts(messageCentreData as InfiniteData<PaginatedResponseType>);
	const messageResultsItemCounts = GetItemCounts(messageResultsData as InfiniteData<PaginatedResponseType>);

	const { selectionActions: singleActions, FilterActions: FilterSingleActions } = useThreadActions();
	const { selectionActions, FilterActions: FilterSelectionActions } = useThreadActions();

	const { mutate: threadUpdates } = useUpdateMessageCentre();

	const ClearFilters = () => {
		navigate('/message-centre/search');
	};

	const UpdateUrlSearchParams = (newParams: URLSearchParams) => {
		setUrlSearchParams(newParams);
	};

	const RemoveUrlSearchParam = (key: string) => {
		urlSearchParams.delete(key);
		UpdateUrlSearchParams(urlSearchParams);
	};

	const UpdateThreadState = (state: boolean) => {
		dispatchMessageCentreStoreUpdate({
			type: 'toggleThreadOpen',
			state,
		});
		addGlobalDocumentValue('message-thread-open', state);
	};

	const DeleteThread = () => {
		const currentThreadId = useMessageCentreStore((state) => state.currentThreadId);
		const messageIds = selectedItems.length > 0 ? selectedItems : [currentThreadId];

		deleteThreads(messageIds)
			.then((r) => {
				if (r.status === 200) {
					threadUpdates({
						newData: {
							messageIds,
							folderName: 'all',
						},
						updateType: MessageItemUpdateType.DELETE,
						skipItemUpdate: false,
					});

					if (currentThreadId && messageIds.includes(currentThreadId)) {
						navigate('/message-centre', { replace: true });
					}
				}

				dispatchMessageCentreStoreUpdate({
					type: 'updateThreadStatus',
					state: false,
				});

				ResetSelection();
				AddNotification({ title: `Deleted ${messageIds.length}`, icon: faGalaxy, colour: Colour.Success });
			})
			.catch((e) => {
				dispatchMessageCentreStoreUpdate({
					type: 'updateThreadStatus',
					state: false,
				});

				ResetSelection();

				AddNotification({
					title: 'Deleting Messages Failed',
					description: e.data?.error,
					manualClose: true,
					icon: faGalaxy,
					colour: Colour.Alert,
				});
			});
	};

	const ArchiveThread = (archive: boolean) => {
		const currentThreadId = useMessageCentreStore((state) => state.currentThreadId);
		const threadIds = selectedItems.length > 0 ? selectedItems : [currentThreadId];

		archiveThreads(threadIds, archive)
			.then((r) => {
				if (r.status === 200) {
					threadUpdates({
						newData: {
							threadIds,
							folderName: 'all',
							isArchived: archive,
						},
						updateType: MessageItemUpdateType.ARCHIVE,
						skipItemUpdate: false,
					});
				}

				dispatchMessageCentreStoreUpdate({
					type: 'updateThreadStatus',
					state: false,
				});

				ResetSelection();

				AddNotification({
					title: archive ? `Archived ${threadIds.length}` : `Un-archived ${threadIds.length}`,
					icon: faGalaxy,
					colour: Colour.Success,
				});
			})
			.catch((e) => {
				dispatchMessageCentreStoreUpdate({
					type: 'updateThreadStatus',
					state: false,
				});

				ResetSelection();

				AddNotification({
					title: 'Change Archive Status Failure',
					description: e.data?.error,
					manualClose: true,
					icon: faGalaxy,
					colour: Colour.Alert,
				});
			});
	};

	const HandleAction = (actionId: number) => {
		dispatchMessageCentreStoreUpdate({
			type: 'updateThreadStatus',
			state: true,
		});

		switch (actionId) {
			case 0:
				ArchiveThread(true);
				break;
			case 1:
				ArchiveThread(false);
				break;
			case 2:
				DeleteThread();
				break;
			default:
		}
	};

	const UpdateThreadStatus = (updating: boolean, newMessageId?: number) => {
		dispatchMessageCentreStoreUpdate({
			type: 'updateThreadStatus',
			state: updating,
		});

		if (newMessageId) {
			dispatchMessageCentreStoreUpdate({
				type: 'updateThreadStatusMessageId',
				messageId: newMessageId,
			});
		}
	};

	const ShowFilters = () => {
		dispatchMessageCentreStoreUpdate({
			type: 'toggleThreadSearchModal',
			state: true,
		});
	};

	useEffect(() => {
		if (messageCentreData) {
			FilterSelectionActions(messageCentreData, selectedItems);
		}
	}, [messageCentreData, selectedItems]);

	useEffect(() => {
		dispatchMessageCentreStoreUpdate({
			type: 'updateCurrentThreadId',
			threadId: params.messageId ? parseInt(params.messageId, 10) : undefined,
		});
	}, [params]);

	const messageCentreMemo = useMemo(
		() => ({
			allSelected,
			visibleSelected,
			visibleIndeterminate,
			selectedItems,
			selectedItemsRef,

			messageCentreData,
			messageCentreError,
			messageCentreIsError,
			messageCentreIsLoading,
			messageCentreIsSuccess,
			messageCentreIsFetchingNextPage,
			messageCentreFetchNextPage,
			messageCentreHasNextPage,
			messageCentreDataUpdatedAt,
			messageCentreIsRefetching,
			messageCentreIsStale,
			messageCentreItemCounts,

			messageResultsData,
			messageResultsError,
			messageResultsIsError,
			messageResultsIsLoading,
			messageResultsIsSuccess,
			messageResultsIsFetchingNextPage,
			messageResultsFetchNextPage,
			messageResultsHasNextPage,
			messageResultsDataUpdatedAt,
			messageResultsIsRefetching,
			messageResultsIsStale,
			messageResultsItemCounts,

			singleActions,
			selectionActions,

			UpdateThreadState,

			FilterSelectionActions,
			FilterSingleActions,

			HandleAction,

			ToggleAll,
			ToggleVisible,
			ToggleSelected,
			ResetSelection,

			ShowFilters,
			ClearFilters,
			UpdateUrlSearchParams,
			RemoveUrlSearchParam,

			UpdateThreadStatus,
		}),
		[
			allSelected,
			visibleSelected,
			visibleIndeterminate,
			selectedItems,
			selectedItemsRef,

			messageCentreData,
			messageCentreError,
			messageCentreIsError,
			messageCentreIsLoading,
			messageCentreIsSuccess,
			messageCentreIsFetchingNextPage,
			messageCentreHasNextPage,
			messageCentreDataUpdatedAt,
			messageCentreIsRefetching,
			messageCentreIsStale,
			messageCentreItemCounts,

			messageResultsData,
			messageResultsError,
			messageResultsIsError,
			messageResultsIsLoading,
			messageResultsIsSuccess,
			messageResultsIsFetchingNextPage,
			messageResultsHasNextPage,
			messageResultsDataUpdatedAt,
			messageResultsIsRefetching,
			messageResultsIsStale,
			messageResultsItemCounts,

			singleActions,
			selectionActions,
		],
	);

	return (
		<MessageCentreContext.Provider value={messageCentreMemo}>
			{props.children}

			<SearchMessageCentreModal
				isOpen={showFilterModal}
				updateFilterValuesCallback={(newParams) => UpdateUrlSearchParams(newParams)}
				closeCallback={() => {
					dispatchMessageCentreStoreUpdate({
						type: 'toggleThreadSearchModal',
						state: false,
					});
				}}
			/>
		</MessageCentreContext.Provider>
	);
}

const useMessageCentre = () => useContext(MessageCentreContext);

export { MessageCentreContext, useMessageCentre };

export default MessageCentreProvider;
